import { useState, useEffect, useRef } from "react";
import { ConfirmationModal } from "../../components/checklists/ConfirmationModal";
import { useNavigate } from "react-router-dom";
import { useGetAllItemsForReviewQuery, useApproveChecklistItemMutation, useRejectChecklistItemMutation } from "../../utils/api/endpoints/checklists/researcherChecklistApi";
import { FiSearch, FiFilter, FiClock, FiCheckCircle, FiXCircle, FiAlertCircle, FiFileText, FiUser, FiCheck, FiX, FiZap, FiMinusCircle, FiLayers } from "react-icons/fi";
import toast from "react-hot-toast";
import CollaborativeResults from "../../components/checklists/CollaborativeResults";

const ChecklistReviewQueue = () => {
  const navigate = useNavigate();
  const { data, isLoading, error } = useGetAllItemsForReviewQuery();

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const [programFilter, setProgramFilter] = useState<string>("ALL");

  const [approveItem, { isLoading: isApproving }] = useApproveChecklistItemMutation();
  const [rejectItem, { isLoading: isRejecting }] = useRejectChecklistItemMutation();
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);

  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: (val?: string) => void;
    type: "info" | "danger" | "warning" | "success";
    confirmLabel?: string;
    showInput?: boolean;
    inputPlaceholder?: string;
    inputValueRequired?: boolean;
  }>({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {},
    type: "info",
  });

  const allItems = data?.data || [];

  // Get unique programs for filter
  const uniquePrograms = Array.from(new Set(allItems.map((i: any) => i.program_title).filter(Boolean))).sort() as string[];

  // Filter items
  const filteredItems = allItems.filter((item) => {
    const programTitle = item.program_title || "";
    const runTitle = item.run_title || "";
    const matchesSearch =
      searchTerm === "" ||
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      programTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
      runTitle.toLowerCase().includes(searchTerm.toLowerCase());

    const effectiveStatus = (!item.outcome_status || item.outcome_status === "NOT_STARTED") ? item.status : item.outcome_status;
    const matchesStatus = statusFilter === "ALL" || effectiveStatus === statusFilter;
    const matchesProgram = programFilter === "ALL" || item.program_title === programFilter;

    return matchesSearch && matchesStatus && matchesProgram;
  });

  // Count by status
  const pendingCount = allItems.filter((i) => i.status === "PENDING_REVIEW").length;
  const completedCount = allItems.filter((i) => {
    const s = (!i.outcome_status || i.outcome_status === "NOT_STARTED") ? i.status : i.outcome_status;
    return s === "COMPLETED";
  }).length;
  const rejectedCount = allItems.filter((i) => i.status === "REJECTED").length;
  const inProgressCount = allItems.filter((i) => {
    const s = (!i.outcome_status || i.outcome_status === "NOT_STARTED") ? i.status : i.outcome_status;
    return s === "IN_PROGRESS";
  }).length;
  const failedCount = allItems.filter((i) => {
    const s = (!i.outcome_status || i.outcome_status === "NOT_STARTED") ? i.status : i.outcome_status;
    return s === "FAILED";
  }).length;
  const naCount = allItems.filter((i) => {
    const s = (!i.outcome_status || i.outcome_status === "NOT_STARTED") ? i.status : i.outcome_status;
    return s === "NOT_APPLICABLE";
  }).length;

  const handleApprove = async (itemId: number) => {
    setConfirmModal({
      isOpen: true,
      title: "Approve Item",
      message: "Are you sure you want to approve this item?",
      type: "success",
      confirmLabel: "Approve",
      onConfirm: async () => {
        try {
          await approveItem({ itemId, comment: "Quick approved from queue" }).unwrap();
          toast.success("Item approved");
        } catch (error: any) {
          toast.error(error?.data?.message || "Failed to approve item");
        }
      }
    });
  };

  const handleReject = async (itemId: number) => {
    setConfirmModal({
      isOpen: true,
      title: "Reject Item",
      message: "Please provide a reason for rejecting this checklist item:",
      type: "danger",
      confirmLabel: "Reject",
      showInput: true,
      inputPlaceholder: "Reason for rejection...",
      inputValueRequired: true,
      onConfirm: async (reason) => {
        if (!reason || !reason.trim()) {
          toast.error("Rejection reason is required");
          return;
        }
        try {
          await rejectItem({ itemId, comment: reason.trim() }).unwrap();
          toast.success("Item rejected");
        } catch (error: any) {
          toast.error(error?.data?.message || "Failed to reject item");
        }
      }
    });
  };

  const handleBulkApprove = async () => {
    const pendingItems = filteredItems.filter(i => i.status === "PENDING_REVIEW");
    if (pendingItems.length === 0) {
      toast.error("No pending items to approve");
      return;
    }

    setConfirmModal({
      isOpen: true,
      title: "Bulk Approve Items",
      message: `Are you sure you want to approve ALL ${pendingItems.length} pending items in the current view?`,
      type: "success",
      confirmLabel: "Bulk Approve",
      onConfirm: async () => {
        setIsBulkProcessing(true);
        let successCount = 0;
        let failCount = 0;

        for (const item of pendingItems) {
          try {
            await approveItem({ itemId: item.program_item_id, comment: "Bulk approved from queue" }).unwrap();
            successCount++;
          } catch (error) {
            failCount++;
          }
        }

        setIsBulkProcessing(false);
        if (successCount > 0) toast.success(`Successfully approved ${successCount} items`);
        if (failCount > 0) toast.error(`Failed to approve ${failCount} items`);
      }
    });
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { bg: string; text: string; border: string; icon: any; label: string }> = {
      PENDING_REVIEW: {
        bg: "bg-yellow-50",
        text: "text-yellow-700",
        border: "border-yellow-200",
        icon: FiClock,
        label: "Pending"
      },
      COMPLETED: {
        bg: "bg-green-50",
        text: "text-green-700",
        border: "border-green-200",
        icon: FiCheckCircle,
        label: "Passed"
      },
      REJECTED: {
        bg: "bg-red-50",
        text: "text-red-700",
        border: "border-red-200",
        icon: FiXCircle,
        label: "Rejected"
      },
      IN_PROGRESS: {
        bg: "bg-blue-50",
        text: "text-blue-700",
        border: "border-blue-200",
        icon: FiClock,
        label: "In Progress"
      },
      FAILED: {
        bg: "bg-red-50",
        text: "text-red-700",
        border: "border-red-200",
        icon: FiXCircle,
        label: "Failed"
      },
      NOT_APPLICABLE: {
        bg: "bg-gray-50",
        text: "text-gray-500",
        border: "border-gray-200",
        icon: FiMinusCircle,
        label: "N/A"
      },
      NOT_STARTED: {
        bg: "bg-gray-50",
        text: "text-gray-700",
        border: "border-gray-200",
        icon: FiClock,
        label: "Not Started"
      }
    };

    const badge = badges[status] || badges.NOT_STARTED;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold border ${badge.bg} ${badge.text} ${badge.border}`}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };


  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-600">Loading review queue...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - No background, matching ChecklistTemplates style */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#0F172A' }}>
                <FiFileText className="w-6 h-6" style={{ color: '#58CC02' }} />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Review Queue</h1>
                <p className="text-sm text-gray-500">Security Test Review</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {pendingCount > 0 && (
                <button
                  onClick={handleBulkApprove}
                  disabled={isBulkProcessing}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#58CC02] to-[#4CAD02] text-white text-sm font-bold rounded-xl hover:shadow-lg hover:shadow-green-200 transition-all active:scale-95 disabled:opacity-50 disabled:active:scale-100 mr-4"
                >
                  <FiZap className={`w-4 h-4 ${isBulkProcessing ? 'animate-pulse' : ''}`} />
                  {isBulkProcessing ? 'Processing...' : 'Bulk Approve All Pending'}
                </button>
              )}
              <span className="text-sm font-medium text-gray-500">Total Queue</span>
              <div className="bg-gray-100 rounded-lg px-4 py-2 min-w-[50px] text-center">
                <span className="text-lg font-bold text-gray-900">{allItems.length}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Completed Card */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300 relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:scale-110 transition-transform">
               <FiCheckCircle size={60} />
            </div>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center border border-green-100">
                <FiCheckCircle className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Passed Tests</p>
                <h3 className="text-3xl font-black text-slate-900 tracking-tight">{completedCount}</h3>
              </div>
            </div>
            <div className="w-full bg-slate-50 h-1.5 rounded-full overflow-hidden">
               <div className="h-full bg-green-500" style={{ width: allItems.length ? `${(completedCount/allItems.length)*100}%` : '0%' }}></div>
            </div>
          </div>

          {/* Failed Card */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300 relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:scale-110 transition-transform">
               <FiXCircle size={60} />
            </div>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center border border-red-100">
                <FiXCircle className="w-6 h-6 text-red-500" />
              </div>
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Failed Tests</p>
                <h3 className="text-3xl font-black text-slate-900 tracking-tight">{failedCount}</h3>
              </div>
            </div>
            <div className="w-full bg-slate-50 h-1.5 rounded-full overflow-hidden">
               <div className="h-full bg-red-500" style={{ width: allItems.length ? `${(failedCount/allItems.length)*100}%` : '0%' }}></div>
            </div>
          </div>

          {/* N/A Card */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300 relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:scale-110 transition-transform">
               <FiMinusCircle size={60} />
            </div>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-xl bg-slate-50 flex items-center justify-center border border-slate-100">
                <FiMinusCircle className="w-6 h-6 text-slate-400" />
              </div>
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Not Applicable</p>
                <h3 className="text-3xl font-black text-slate-900 tracking-tight">{naCount}</h3>
              </div>
            </div>
            <div className="w-full bg-slate-50 h-1.5 rounded-full overflow-hidden">
               <div className="h-full bg-slate-300" style={{ width: allItems.length ? `${(naCount/allItems.length)*100}%` : '0%' }}></div>
            </div>
          </div>
        </div>

        {/* Filters Section - Premium Dark Style */}
        <div className="rounded-2xl p-6 mb-8 shadow-xl" style={{ backgroundColor: '#0F172A', backgroundImage: 'radial-gradient(circle at top right, rgba(88, 204, 2, 0.05), transparent 300px)' }}>
          {/* Header Row */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-800 rounded-lg">
                <FiFilter className="w-4 h-4" style={{ color: '#58CC02' }} />
              </div>
              <div>
                <span className="text-sm font-semibold text-white uppercase tracking-widest">Filter Results</span>
                <p className="text-[10px] text-slate-400 font-semibold">Narrow down your review queue</p>
              </div>
            </div>
            <div className="px-3 py-1 bg-slate-800 rounded-full border border-slate-700">
               <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-widest">
                Showing <span className="text-white">{filteredItems.length}</span> of <span className="text-white">{allItems.length}</span> items
               </p>
            </div>
          </div>

          {/* Filter Inputs Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Search Input */}
            <div className="relative group">
              <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#58CC02] transition-colors" />
              <input
                type="text"
                placeholder="Search items or programs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3.5 bg-slate-800 border border-slate-700/50 rounded-xl text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-[#58CC02]/20 focus:border-[#58CC02]/50 transition-all shadow-inner"
              />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-3.5 bg-slate-800 border border-slate-700/50 rounded-xl text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-[#58CC02]/20 focus:border-[#58CC02]/50 appearance-none cursor-pointer transition-all shadow-inner font-semibold"
                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23475569'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 16px center', backgroundSize: '16px' }}
              >
                <option value="ALL">All Test Results</option>
                <option value="PENDING_REVIEW">Pending Review</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="COMPLETED">Passed Tests</option>
                <option value="FAILED">Failed Tests</option>
                <option value="NOT_APPLICABLE">N/A Tests</option>
                <option value="REJECTED">Rejected Tests</option>
                <option value="NOT_STARTED">Not Started</option>
              </select>
            </div>
 
            {/* Program Filter */}
            <div className="relative">
              <select
                value={programFilter}
                onChange={(e) => setProgramFilter(e.target.value)}
                className="w-full px-4 py-3.5 bg-slate-800 border border-slate-700/50 rounded-xl text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-[#58CC02]/20 focus:border-[#58CC02]/50 appearance-none cursor-pointer transition-all shadow-inner font-semibold"
                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23475569'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 16px center', backgroundSize: '16px' }}
              >
                <option value="ALL">All Active Programs</option>
                {uniquePrograms.map((prog: string) => (
                  <option key={prog} value={prog}>{prog}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Compact Items List */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
          {allItems.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                <FiAlertCircle className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-2">No Checklist Items Yet</h3>
              <p className="text-sm text-gray-600 mb-4 max-w-md mx-auto">
                Get started by creating checklist templates and linking them to programs
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-lg mx-auto text-left">
                <p className="text-xs font-semibold text-blue-900 mb-2">Quick Start:</p>
                <ol className="text-xs text-blue-800 space-y-1.5">
                  <li className="flex items-start gap-2">
                    <span className="flex-shrink-0 w-4 h-4 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">1</span>
                    <span>Create a checklist template</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex-shrink-0 w-4 h-4 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">2</span>
                    <span>Link template to program</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex-shrink-0 w-4 h-4 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">3</span>
                    <span>Assign researchers</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex-shrink-0 w-4 h-4 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">4</span>
                    <span>Researchers submit for review</span>
                  </li>
                </ol>
              </div>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <FiSearch className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-2">No Matching Items</h3>
              <p className="text-sm text-gray-600 mb-3">Try adjusting your search or filter settings</p>
              <button
                onClick={() => {
                  setSearchTerm("");
                  setStatusFilter("ALL");
                }}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
              >
                Clear All Filters
              </button>
            </div>
          ) : (
            <div className="divide-y divide-slate-100 bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
               {/* Custom Header */}
               <div className="bg-slate-50/50 px-5 py-2.5 border-b border-slate-100 hidden lg:flex items-center text-[9px] font-black text-slate-400 uppercase tracking-[0.15em]">
                  <div className="flex-1">Security Requirement</div>
                  <div className="w-[180px] px-4">Source</div>
                  <div className="w-[120px] px-4 text-center">Result</div>
                  <div className="w-[100px] px-2 text-center">Researchers</div>
                  <div className="w-[140px] text-right">Actions</div>
               </div>

               {filteredItems.map((item) => (
                  <div 
                    key={item.program_item_id}
                    onClick={() => navigate(`/dashboard/checklist-review/items/${item.program_item_id}`)}
                    className="group px-5 py-3.5 flex flex-col lg:flex-row lg:items-center hover:bg-blue-50/30 transition-all cursor-pointer border-b last:border-0 border-slate-50"
                  >
                    {/* Item Details */}
                    <div className="flex-1 min-w-0 lg:pr-4">
                      <div className="flex items-start gap-2 mb-1.5">
                        {item.requirement_id && (
                          <span className="mt-0.5 bg-slate-900 text-[#58CC02] text-[8px] font-black px-1.5 py-0.5 rounded border border-slate-800 shrink-0 tabular-nums">
                            {item.requirement_id}
                          </span>
                        )}
                        <h4 className="text-[13px] font-bold text-slate-900 group-hover:text-blue-600 transition-colors leading-[1.45] whitespace-normal break-words">
                          {item.title}
                        </h4>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 pl-0 lg:pl-1">
                        {item.category && (
                          <span className="flex items-center gap-1 text-[8px] font-black text-slate-500 uppercase tracking-wider">
                            {item.category}
                          </span>
                        )}
                        <div className="flex items-center gap-1">
                          {item.framework === "ASVS" && (
                            <div className="flex gap-1">
                              {["l1", "l2", "l3"].map(level => {
                                if (!(item as any)[level]) return null;
                                return (
                                  <span key={level} className="bg-green-50 text-green-600 text-[8px] font-black px-1 py-0.5 rounded border border-green-100 uppercase tabular-nums">
                                    {level}
                                  </span>
                                );
                              })}
                            </div>
                          )}
                          {(item.is_web || item.is_api) && (
                            <div className="flex gap-1">
                              {item.is_web && <span className="bg-blue-50 text-blue-500 text-[8px] font-black px-1 py-0.5 rounded border border-blue-50 uppercase tracking-tighter">Web</span>}
                              {item.is_api && <span className="bg-purple-50 text-purple-500 text-[8px] font-black px-1 py-0.5 rounded border border-purple-50 uppercase tracking-tighter">API</span>}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Testing Context */}
                    <div className="w-full lg:w-[180px] px-0 lg:px-4 py-2 lg:py-0 border-t lg:border-t-0 lg:border-l border-slate-50 mt-2 lg:mt-0">
                       <div className="flex flex-col">
                          <p className="text-[11px] text-slate-800 font-bold leading-tight group-hover:text-slate-950 transition-colors truncate">
                            {item.run_title || "Master Checklist"}
                          </p>
                          <span className="text-[9px] font-black text-slate-400 capitalize truncate opacity-80">
                            {item.program_title}
                          </span>
                       </div>
                    </div>

                    {/* Result */}
                    <div className="w-full lg:w-[120px] px-0 lg:px-4 py-2 lg:py-0 flex items-center lg:justify-center border-t lg:border-t-0 lg:border-l border-slate-50">
                       <div className="scale-90 origin-left lg:origin-center">
                          {getStatusBadge((!item.outcome_status || item.outcome_status === "NOT_STARTED") ? item.status : item.outcome_status)}
                       </div>
                    </div>

                    {/* Ownership (Avatars Only) */}
                    <div className="w-full lg:w-[100px] px-0 lg:px-2 py-2 lg:py-0 border-t lg:border-t-0 lg:border-l border-slate-50 flex items-center justify-start lg:justify-center">
                       {item.all_results && item.all_results.length > 0 ? (
                          <div className="scale-[0.85] origin-center shadow-sm rounded-full bg-white p-0.5"><CollaborativeResults item={item} /></div>
                       ) : (
                          <div className="w-6 h-6 bg-slate-100 rounded-full flex items-center justify-center text-slate-300 transform group-hover:scale-110 transition-transform">
                            <FiUser className="w-3.5 h-3.5" />
                          </div>
                       )}
                    </div>

                    {/* Actions */}
                    <div className="w-full lg:w-[140px] py-3 lg:py-0 pl-0 lg:pl-4 border-t lg:border-t-0 lg:border-l border-slate-50 flex items-center justify-end gap-2">
                       {item.status === "PENDING_REVIEW" && (
                          <div className="flex items-center gap-1.5">
                             <button
                               onClick={(e) => {
                                 e.stopPropagation();
                                 handleApprove(item.program_item_id);
                               }}
                               disabled={isApproving}
                               className="w-7 h-7 bg-green-50 text-green-600 rounded-lg hover:bg-green-600 hover:text-white transition-all border border-green-100 flex items-center justify-center shadow-sm"
                               title="Approve"
                             >
                               <FiCheck className="w-3.5 h-3.5" />
                             </button>
                             <button
                               onClick={(e) => {
                                 e.stopPropagation();
                                 handleReject(item.program_item_id);
                               }}
                               disabled={isRejecting}
                               className="w-7 h-7 bg-red-50 text-red-600 rounded-lg hover:bg-red-600 hover:text-white transition-all border border-red-100 flex items-center justify-center shadow-sm"
                               title="Reject"
                             >
                               <FiX className="w-3.5 h-3.5" />
                             </button>
                          </div>
                       )}
                       <button
                         onClick={(e) => {
                           e.stopPropagation();
                           navigate(`/dashboard/checklist-review/items/${item.program_item_id}`);
                         }}
                         className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 text-[#58CC02] text-[10px] font-black rounded-lg hover:bg-slate-800 transition-all border border-slate-700 uppercase tracking-tighter"
                       >
                         Review
                       </button>
                    </div>
                  </div>
               ))}
            </div>
          )}
        </div>
      </div>
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmLabel={confirmModal.confirmLabel}
        type={confirmModal.type}
        showInput={confirmModal.showInput}
        inputPlaceholder={confirmModal.inputPlaceholder}
        inputValueRequired={confirmModal.inputValueRequired}
      />
    </div>
  );
};

export default ChecklistReviewQueue;

